import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../hooks/useAuth';
import { MEETING_PREP_CONFIG } from './config';
import { useMeetingPrep } from './useMeetingPrep';
import downloadBriefingAsPdf from './pdf';
import linkifyUids from './linkify';
import { Citation } from './types';
import './MeetingPrep.scss';

const LOADING_PHRASES = [
  'Reading the workflow…',
  'Collecting contested annotations…',
  'Comparing reviewer votes…',
  'Searching for precedent…',
  'Assembling the briefing…',
];

const formatWhen = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return '';
  }
};

/**
 * Meeting-Prep briefing drawer. Mounted once from App.tsx and decoupled from the trigger via a
 * window event ('meeting-prep:open' carrying { workflowId }). The briefing is generated once per
 * workflow and cached for the session (see useMeetingPrep) — reopening serves the cached copy with
 * no API call; Regenerate forces a fresh one. Removable: delete this folder + the App.tsx mount +
 * the MeetingPrepButton import.
 */
const MeetingPrepPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [workflowId, setWorkflowId] = useState('');
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const {
    briefing, loading, error, show, regenerate,
  } = useMeetingPrep();

  // Open from the AnnotationStudio / Workflows-list button (decoupled — no shared context).
  useEffect(() => {
    const onOpen = (e: Event) => {
      const id = (e as CustomEvent).detail?.workflowId;
      if (!id) return;
      setWorkflowId(id);
      setOpen(true);
      setCopied(false);
      show(id); // cached if already generated this session
    };
    window.addEventListener('meeting-prep:open', onOpen);
    return () => window.removeEventListener('meeting-prep:open', onOpen);
  }, [show]);

  // Rotate the loading phrase while the (possibly slow) request is in flight.
  useEffect(() => {
    if (!loading) {
      setPhraseIndex(0);
      return undefined;
    }
    const id = setInterval(() => {
      setPhraseIndex((i) => (i + 1) % LOADING_PHRASES.length);
    }, 2000);
    return () => clearInterval(id);
  }, [loading]);

  const onCitation = useCallback((path: string) => {
    setOpen(false);
    navigate(path);
  }, [navigate]);

  // Copy the rendered briefing to the clipboard as formatted HTML (+ plain-text fallback) so the
  // user can paste it straight into an email until a native send-email feature exists.
  const copyBriefing = useCallback(async () => {
    const el = bodyRef.current;
    const html = el?.innerHTML || '';
    const text = el?.innerText || el?.textContent || briefing?.reply || '';
    try {
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        await navigator.clipboard.write([new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        })]);
      } else {
        await navigator.clipboard?.writeText(text);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — ignore */
    }
  }, [briefing]);

  if (!MEETING_PREP_CONFIG.ENABLED || !user || !MEETING_PREP_CONFIG.ALLOWED_ROLES.includes(user.role)) {
    return null;
  }

  // In-app routes (href starting with '/') navigate via onCitation; external links open normally.
  const mdComponents = {
    a: ({ href, children }: any) => (href && href.startsWith('/') ? (
      <a href={href} onClick={(e) => { e.preventDefault(); onCitation(href); }}>{children}</a>
    ) : (
      <a href={href} target="_blank" rel="noreferrer">{children}</a>
    )),
  };

  const header = (
    <div className="mp-drawer-head">
      <span className="mp-drawer-title">
        <i className="pi pi-calendar" />
        {' '}
        Meeting Briefing
      </span>
      {!loading && !error && briefing && (
        <div className="mp-head-actions">
          <button type="button" className="mp-head-icon" onClick={copyBriefing} title="Copy briefing (for email)">
            <i className={copied ? 'pi pi-check' : 'pi pi-copy'} />
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button type="button" className="mp-head-icon" onClick={() => regenerate(workflowId)} title="Regenerate the briefing">
            <i className="pi pi-refresh" />
            Regenerate
          </button>
          <button type="button" className="mp-header-pdf" onClick={() => downloadBriefingAsPdf(briefing.reply)} title="Download this briefing as a PDF">
            <i className="pi pi-file-pdf" />
            <span>Download PDF</span>
          </button>
        </div>
      )}
    </div>
  );

  return (
    <Sidebar
      className="mp-drawer"
      visible={open}
      position="right"
      onHide={() => setOpen(false)}
      header={header}
      blockScroll
    >
      {loading && (
        <div className="mp-loading">
          <i className="pi pi-spin pi-spinner" />
          <span>{LOADING_PHRASES[phraseIndex]}</span>
        </div>
      )}

      {!loading && error && (
        <div className="mp-error">
          <i className="pi pi-exclamation-triangle" />
          <p>{error}</p>
          <Button label="Retry" icon="pi pi-refresh" onClick={() => regenerate(workflowId)} />
        </div>
      )}

      {!loading && !error && briefing && (
        <>
          <div className="mp-generated">
            {`Generated ${formatWhen(briefing.generatedAt)} · cached for this session`}
          </div>

          <div className="mp-briefing mp-markdown" ref={bodyRef}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
              {linkifyUids(briefing.reply, briefing.citations)}
            </ReactMarkdown>
          </div>

          {briefing.citations.length > 0 && (
            <div className="mp-citations">
              {briefing.citations.map((c: Citation) => (
                <button
                  key={`${c.label}-${c.path}`}
                  type="button"
                  className="mp-citation"
                  disabled={!c.path}
                  onClick={() => c.path && onCitation(c.path)}
                >
                  <i className="pi pi-external-link" />
                  {' '}
                  {c.label}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </Sidebar>
  );
};

export default MeetingPrepPanel;
